/* eslint-disable */
import React, { useEffect, useRef } from "react";
import * as THREE from "three";
import ThreeGlobe from "three-globe";

/**
 * ConnexisGlobe v5 — "Earth rising at sunrise" matching the brand wallpaper.
 *
 *  Direction (per user reference image #3):
 *    - Globe is HALF-BURIED at the bottom (only top hemisphere visible).
 *    - Bright BLUE SUNRISE GLOW at the top of the globe (horizon light).
 *    - DEEP space background with many stars sparkling.
 *    - SOFT, sparse curved arcs — not aggressive crisscrossing.
 *    - Bright cyan city beacons twinkling on the surface ("magical sparks").
 */

const NODES = [
  { lat: 51.5074, lng: -0.1278 },   // London
  { lat: 40.7128, lng: -74.006 },   // New York
  { lat: 37.7749, lng: -122.419 },  // San Francisco
  { lat: 48.8566, lng: 2.3522 },    // Paris
  { lat: 52.5200, lng: 13.4050 },   // Berlin
  { lat: 40.4168, lng: -3.7038 },   // Madrid
  { lat: 41.0082, lng: 28.9784 },   // Istanbul
  { lat: 25.276, lng: 55.296 },     // Dubai
  { lat: 19.076, lng: 72.8777 },    // Mumbai
  { lat: 1.3521, lng: 103.8198 },   // Singapore
  { lat: 22.3193, lng: 114.1694 },  // Hong Kong
  { lat: 35.6762, lng: 139.6503 }, // Tokyo
  { lat: -33.8688, lng: 151.2093 },// Sydney
  { lat: -26.2041, lng: 28.0473 },  // Johannesburg
  { lat: 25.7617, lng: -80.1918 }, // Miami
  { lat: -23.5505, lng: -46.6333 },// São Paulo
  { lat: 28.6139, lng: 77.209 },   // Delhi
  { lat: 55.7558, lng: 37.6173 },  // Moscow
  { lat: 30.0444, lng: 31.2357 },  // Cairo
  { lat: 31.2304, lng: 121.4737 },// Shanghai
];

// Hand-picked elegant arc routes — soft sweeping connections, not crisscrossing
const FIXED_ARCS = [
  // Atlantic
  { startLat: 51.5, startLng: -0.1,   endLat: 40.7, endLng: -74.0 },  // LON ↔ NYC
  // Europe ↔ Asia
  { startLat: 51.5, startLng: -0.1,   endLat: 35.7, endLng: 139.7 }, // LON ↔ TYO
  // US east-west
  { startLat: 40.7, startLng: -74.0,  endLat: 37.8, endLng: -122.4 },// NYC ↔ SF
  // Middle East corridor
  { startLat: 51.5, startLng: -0.1,   endLat: 25.3, endLng: 55.3 },  // LON ↔ DXB
  // SE Asia
  { startLat: 25.3, startLng: 55.3,   endLat: 1.4,  endLng: 103.8 }, // DXB ↔ SIN
  // South America
  { startLat: 40.7, startLng: -74.0,  endLat: -23.5, endLng: -46.6 },// NYC ↔ SAO
  // Pacific
  { startLat: 37.8, startLng: -122.4, endLat: 35.7, endLng: 139.7 },// SF ↔ TYO
  // Africa
  { startLat: 51.5, startLng: -0.1,   endLat: -26.2, endLng: 28.0 },// LON ↔ JNB
];

function makeRings(count) {
  const out = [];
  const used = new Set();
  while (out.length < count) {
    const idx = Math.floor(Math.random() * NODES.length);
    if (used.has(idx)) continue;
    used.add(idx);
    const n = NODES[idx];
    out.push({ lat: n.lat, lng: n.lng });
  }
  return out;
}

export default function ConnexisGlobe({ className = "" }) {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const W = mount.clientWidth;
    const H = mount.clientHeight;
    const isMobile = window.matchMedia("(max-width: 768px)").matches;

    const scene = new THREE.Scene();
    // Camera centred on the globe — full sphere visible with margin
    const camera = new THREE.PerspectiveCamera(35, W / H, 0.1, 2000);
    camera.position.set(0, 0, 460);
    camera.lookAt(0, 0, 0);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: "high-performance" });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, isMobile ? 1.75 : 2.25));
    renderer.setSize(W, H);
    renderer.setClearColor(0x000000, 0);
    mount.appendChild(renderer.domElement);

    // ---- DENSE starfield (the "real universe in the stars" vibe) ----
    const starCount = isMobile ? 1800 : 3500;
    const starGeo = new THREE.BufferGeometry();
    const starPos = new Float32Array(starCount * 3);
    const starSize = new Float32Array(starCount);
    for (let i = 0; i < starCount; i++) {
      const r = 500 + Math.random() * 800;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      starPos[i * 3]     = r * Math.sin(phi) * Math.cos(theta);
      starPos[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      starPos[i * 3 + 2] = r * Math.cos(phi);
      starSize[i] = Math.random() * 1.4 + 0.3;
    }
    starGeo.setAttribute("position", new THREE.BufferAttribute(starPos, 3));
    starGeo.setAttribute("size", new THREE.BufferAttribute(starSize, 1));
    const starMat = new THREE.PointsMaterial({
      color: 0xffffff,
      size: 1.1,
      transparent: true,
      opacity: 0.75,
      sizeAttenuation: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    const stars = new THREE.Points(starGeo, starMat);
    scene.add(stars);

    // ---- SUBTLE inner glow — sits right behind the planet, much smaller now
    //      so the blue "shine" is compressed into a thin atmospheric band that
    //      hugs the globe edge (like a real Earth atmosphere from space).
    const glowCanvas = document.createElement("canvas");
    glowCanvas.width = 1024; glowCanvas.height = 1024;
    const gctx = glowCanvas.getContext("2d");
    const grad = gctx.createRadialGradient(512, 512, 280, 512, 512, 420);
    grad.addColorStop(0,    "rgba(0, 0, 0, 0)");
    grad.addColorStop(0.55, "rgba(70, 140, 255, 0.45)");
    grad.addColorStop(0.75, "rgba(40, 100, 230, 0.22)");
    grad.addColorStop(1,    "rgba(0, 20, 80, 0)");
    gctx.fillStyle = grad;
    gctx.fillRect(0, 0, 1024, 1024);
    const glowTex = new THREE.CanvasTexture(glowCanvas);
    const glow = new THREE.Mesh(
      new THREE.PlaneGeometry(340, 340),
      new THREE.MeshBasicMaterial({ map: glowTex, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false })
    );
    glow.position.set(0, 0, -40);
    scene.add(glow);

    // ---- lights ----
    scene.add(new THREE.AmbientLight(0xffffff, 0.6));
    const key = new THREE.DirectionalLight(0xffffff, 1.5);
    key.position.set(0, 5, 4);
    scene.add(key);
    const rim = new THREE.DirectionalLight(0x4d8fff, 1.2);
    rim.position.set(0, 8, -2); // rim from above-back for the "sunrise"
    scene.add(rim);

    // ---- globe (WHITE dotted continents — fixed colour, no pulsing) ----
    const globe = new ThreeGlobe({ animateIn: false })
      .showGlobe(true)
      .showAtmosphere(true)
      .atmosphereColor("#5b9eff")
      .atmosphereAltitude(0.14)
      .hexPolygonResolution(isMobile ? 3 : 4)
      .hexPolygonMargin(0.28)
      .hexPolygonUseDots(true)
      .hexPolygonColor(() => "#ffffff")
      // City sparkles
      .pointsData(NODES)
      .pointLat("lat")
      .pointLng("lng")
      .pointColor(() => "#ffffff")
      .pointAltitude(0.012)
      .pointRadius(0.45)
      .pointResolution(8)
      // Soft, sparse curved arcs — 8 hand-picked corridors only, BRAND BLUE
      .arcsData(FIXED_ARCS)
      .arcColor(() => "rgba(61, 123, 255, 0.95)")
      .arcAltitude(() => 0.22 + Math.random() * 0.12)
      .arcStroke(0.42)
      .arcDashLength(0.55)
      .arcDashGap(1.2)
      .arcDashAnimateTime(2800)
      // Pulse rings — brand-blue magical sparks at random cities
      .ringsData(makeRings(4))
      .ringColor(() => (rT) => `rgba(61, 123, 255, ${1 - rT})`)
      .ringMaxRadius(4.5)
      .ringPropagationSpeed(2.2)
      .ringRepeatPeriod(700);

    // Ocean — visible navy that gives the planet a clear circular edge
    const ocean = globe.globeMaterial();
    ocean.color = new THREE.Color("#0e1f4d");
    ocean.emissive = new THREE.Color("#081a40");
    ocean.emissiveIntensity = 0.75;

    // Style dots — solid WHITE with constant emissive (no colour shift)
    const styleDots = () => {
      globe.traverse((obj) => {
        if (obj.isMesh && obj.material && obj.material.isMeshLambertMaterial) {
          obj.material.color = new THREE.Color("#ffffff");
          obj.material.emissive = new THREE.Color("#ffffff");
          obj.material.emissiveIntensity = 0.9;
          obj.material.transparent = true;
          obj.material.opacity = 1;
        }
      });
    };
    setTimeout(styleDots, 250);
    setTimeout(styleDots, 1100);
    setTimeout(styleDots, 2500);

    // FULL globe visible — slight tilt for personality, no burying
    globe.scale.set(1.05, 1.05, 1.05);
    globe.position.y = 0;
    globe.rotation.x = THREE.MathUtils.degToRad(-12);
    globe.rotation.y = THREE.MathUtils.degToRad(-30); // Africa + Europe forward
    scene.add(globe);

    // Country GeoJSON for the dotted continent pattern
    fetch("/data/world-110m.geojson")
      .then((r) => r.json())
      .then((geo) => {
        globe.hexPolygonsData(geo.features);
        setTimeout(styleDots, 200);
        setTimeout(styleDots, 1000);
      })
      .catch(() => {});

    // Refresh radar rings every 1.4s — different cities pulse continuously
    const ringInterval = setInterval(() => {
      globe.ringsData(makeRings(4));
    }, 1400);

    // ---- raf loop ----
    let rafId = 0;
    const start = performance.now();
    const animate = () => {
      const rect = mount.getBoundingClientRect();
      const visible = rect.bottom > 0 && rect.top < window.innerHeight;
      if (visible) {
        const t = (performance.now() - start) / 1000;

        // Slow luxurious rotation
        globe.rotation.y += 0.0018;

        // Dots stay constant pure white (NO pulsing colour shift)
        // — emissive intensity was previously varied here which made them
        //   appear to change colour. Now fixed at 0.9 (set once in styleDots).

        // Inner atmosphere glow breathes very subtly
        glow.material.opacity = 0.85 + Math.sin(t * 0.4) * 0.08;

        // Star twinkle
        stars.material.opacity = 0.62 + Math.sin(t * 0.7) * 0.18;

        renderer.render(scene, camera);
      }
      rafId = requestAnimationFrame(animate);
    };
    animate();

    const handleResize = () => {
      const w = mount.clientWidth;
      const h = mount.clientHeight;
      if (w === 0 || h === 0) return;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h, false);
    };
    window.addEventListener("resize", handleResize);
    // ResizeObserver catches canvas-size changes that DON'T fire a window resize
    // (e.g. CSS breakpoints changing, layout shifts, font load). This is what
    // caused the globe to render as an oval on some viewports.
    const ro = new ResizeObserver(handleResize);
    ro.observe(mount);

    return () => {
      cancelAnimationFrame(rafId);
      clearInterval(ringInterval);
      window.removeEventListener("resize", handleResize);
      ro.disconnect();
      try { mount.removeChild(renderer.domElement); } catch (e) {}
      renderer.dispose();
      starGeo.dispose();
      glowTex.dispose();
    };
  }, []);

  return <div ref={mountRef} className={`size-full ${className}`} data-testid="connexis-globe-canvas" />;
}
